import React, { useEffect, useState } from "react";
import { useToast } from "../context/ToastContext";
import { getMyReferralLink, getMyReferrals } from "../api";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const STAGE_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  new:         { bg: "#e0f2fe", fg: "#0369a1", label: "Applied" },
  reviewed:    { bg: "#fef3c7", fg: "#b45309", label: "Under Review" },
  shortlisted: { bg: "#ede9fe", fg: "#6d28d9", label: "Shortlisted" },
  hired:       { bg: "#dcfce7", fg: "#15803d", label: "Hired 🎉" },
  rejected:    { bg: "#fee2e2", fg: "#b91c1c", label: "Not Selected" },
};

export default function ReferralsPage() {
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const [link, setLink] = useState("");
  const [careerPage, setCareerPage] = useState("");
  const [referrals, setReferrals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getMyReferralLink(), getMyReferrals()])
      .then(([l, r]) => { setLink(l.link); setCareerPage(l.career_page); setReferrals(r); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function copyLink() {
    navigator.clipboard.writeText(link).then(() => toast("Referral link copied!", "success"));
  }

  const hired = referrals.filter(r => r.status === "hired").length;

  return (
    <div>
      {/* Width and padding come from ShellMain. This is a list page like
          Candidates, Jobs, Offers and Talent Pool — all full width — so a
          cap here left it visibly narrower than its siblings, and its own
          padding stacked on the shell's. */}
      {/* The house `.page-header` block. A hand-rolled navy <h2> here read as a
          different kind of heading from the black <h1> every sibling page uses. */}
      <div className="page-header">
        <div>
          <h1>Refer a Friend</h1>
          <p>
            Share your personal link — anyone who applies through it is tracked as your referral
          </p>
        </div>
      </div>

      {/* Referral link card */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY}, #0f2c6b)`, borderRadius: 16, padding: 28, marginBottom: 28, color: "white" }}>
        <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.8, marginBottom: 10 }}>YOUR REFERRAL LINK</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <code style={{ background: "rgba(255,255,255,0.12)", padding: "10px 16px", borderRadius: 8, fontSize: 13, flex: "1 1 200px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {loading ? "…" : link}
          </code>
          <button onClick={copyLink}
            style={{ background: TEAL, color: "#06302a", border: "none", padding: "10px 22px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            📋 Copy
          </button>
        </div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>
          Open positions are listed on <a href={careerPage} target="_blank" rel="noreferrer" style={{ color: TEAL }}>our careers page</a> — share the link on WhatsApp, LinkedIn, anywhere.
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: NAVY }}>{referrals.length}</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>Total referrals</div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#15803d" }}>{hired}</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>Hired 🎉</div>
        </div>
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#6d28d9" }}>
            {referrals.filter(r => !["hired", "rejected"].includes(r.status)).length}
          </div>
          <div style={{ fontSize: 13, color: "#64748b" }}>In progress</div>
        </div>
      </div>

      {/* Referrals list */}
      <h3 style={{ fontSize: 15, color: "#334155", marginBottom: 12 }}>My Referrals</h3>
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
      ) : referrals.length === 0 ? (
        <div style={{ textAlign: "center", padding: 50, color: "#94a3b8", background: "#f8fafc", borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>🤝</div>
          <div style={{ marginTop: 10 }}>No referrals yet. Share your link to get started!</div>
        </div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          {referrals.map((r, i) => {
            const stage = STAGE_COLORS[r.status] || { bg: "#f1f5f9", fg: "#475569", label: r.status };
            return (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderTop: i > 0 ? "1px solid #f1f5f9" : "none", flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.candidate_name}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    {r.job_title} · applied {formatDate(r.applied_at, "short", tz)}
                  </div>
                </div>
                <span style={{ background: stage.bg, color: stage.fg, padding: "4px 12px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
